import { Box } from '@mui/material';
import { Plus, Trash } from '@styled-icons/bootstrap';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import ProductSummarySkeleton from 'FeatureModules/ProductPage/Components/Tables/ProductSummarySkeleton';
import { CardIllustration, EmptyStateCard, ThemeButton } from 'DesignSystem';
import React, { FC } from 'react';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import ComponentChainTable from 'FeatureModules/ComponentChain/Components/Table/ComponentChainTable';
import { ITableRowAction } from 'FeatureModules/ComponentChain/Components/Table/TableRow';
import { Edit } from '@styled-icons/boxicons-solid';
import { useNavigate } from 'react-router-dom';
import useComponentChainActions from 'FeatureModules/ComponentChain/hooks/useComponentChainActions';
import useDialog from 'hooks/useDialog';
import { ECOMPONENT_CHAIN_COLUMN_IDS } from 'FeatureModules/ComponentChain/utils/headerColumns';
import usePartnerComponentChains from 'FeatureModules/Partner/hooks/usePartnerComponentChains';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ScrollableTableContainer from 'Components/ScrollableTableContainer';
import PageContainer from 'Components/PageContainer';
import ErrorState from 'Components/ErrorState';

const PartnerComponentChains: FC = () => {
  const navigate = useNavigate();
  const {
    loading,
    error,
    filteredComponentChains,
    componentChains,
    searchTerm,
    handleCreateComponentChain,
    setSearchTerm,
  } = usePartnerComponentChains();
  const { deleteChain: deleteComponentChain } = useComponentChainActions();
  const { openDialog, closeDialog } = useDialog();

  const handleDeleteComponentChain = async (id: string) => {
    const componentChain = filteredComponentChains.find(chain => chain.id === id);
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete component chain',
        itemTitle: componentChain?.title,
        displayCloseButton: true,
        text: 'Are you sure you want to delete this component chain? All data will be lost and you will not be able to recover this item.',
        submitText: 'Delete',
        onSubmit: () => {
          deleteComponentChain({ variables: { id } });
        },
        onCancel: () => closeDialog('ALERT'),
      },
    });
  };

  const handleRowClick = (id: string) => {
    navigate(`/component-chains/${id}`);
  };

  const handleEditClick = (id: string) => {
    const componentChain = filteredComponentChains.find(chain => chain.id === id);
    if (!componentChain) {
      console.error('Can not find component chain');
      return;
    }
    openDialog({
      type: 'EDIT_CHAIN',
      props: { chain: componentChain },
    });
  };

  const componentTableActions: ITableRowAction[] = [
    {
      renderIcon: () => <Edit size="16" />,
      tooltip: 'Edit component chain',
      onClick: handleEditClick,
      dataCy: 'update-component-chain-btn',
    },
    {
      renderIcon: () => <Trash size="16" />,
      tooltip: 'Delete component chain',
      onClick: (id: string) => handleDeleteComponentChain(id),
      disableIfUsed: true,
      disableIfPartOfRequest: true,
      disabledTooltip:
        'Component chain is part of another supply chain or part of a partner request',
      dataCy: 'delete-component-chain-btn',
    },
  ];

  if (loading) return <Loader />;
  if (error) return <ErrorState />;

  if (!componentChains.length) {
    return (
      <Box mt={4}>
        <EmptyStateCard
          title="Map your component chains to apply and re-use in your product supply chains"
          onCreateItemClick={handleCreateComponentChain}
          variant={EItemTypes.COMPONENT_CHAIN}
          buttonText="Add component chain"
          background={EBackgroundTypes.WORLD}
          size={EAvailableSizes.LARGE}
          renderIllustration={
            <CardIllustration image={EIllustrationTypes.LOCATIONS} width={300} right={25} top={0} />
          }
          data-testid="component-chains-getting-started-card"
          data-testid-button="component-chains-getting-started-card-button"
        />
      </Box>
    );
  }

  return (
    <PageContainer>
      <ActionContainer>
        <SearchToolbar
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm || ''}
          className="search-input"
          data-testid="component-chain-search-input"
          placeholder="Search component"
          actions={
            <Box ml={1}>
              <ThemeButton
                size="medium"
                color="PRIMARY"
                onClick={handleCreateComponentChain}
                startIcon={<Plus size={20} color="white" />}
                data-testid="create-component-chain-btn"
              >
                Create new component chain
              </ThemeButton>
            </Box>
          }
        />
      </ActionContainer>
      <ScrollableTableContainer pb={1}>
        <ComponentChainTable
          displayTableHeader
          columnKeys={[
            ECOMPONENT_CHAIN_COLUMN_IDS.TITLE,
            ECOMPONENT_CHAIN_COLUMN_IDS.INVOLVED_PARTNERS,
            ECOMPONENT_CHAIN_COLUMN_IDS.ACTIONS,
          ]}
          componentChains={filteredComponentChains}
          actions={componentTableActions}
          onRowClick={handleRowClick}
        />
      </ScrollableTableContainer>

      {/* No Result container */}
      {!filteredComponentChains.length && (
        <EmptySearchResults
          message="No search results found"
          data-testid="component-chain-no-results"
          action={{
            label: 'Create component chain',
            onClick: handleCreateComponentChain,
          }}
          skeletonComponent={<ProductSummarySkeleton title={searchTerm} />}
        />
      )}
    </PageContainer>
  );
};

export default PartnerComponentChains;
